import React, { lazy, Suspense, useEffect, useCallback } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';

import * as ROUTES from 'constants/routes.js';

import { OrgShortcut, ProfileShortcut, ClientShortcut } from 'pages/Shortcuts/Shortcuts.jsx';
import NotFoundPage from 'pages/NotFound/index.jsx';
import OrgRouter from './OrgRouter.jsx';
import OrgRedirect from './OrgRedirect.js';
import ErrorFallback from './ErrorFallback.jsx';
import LogRocket, { initLogRocket } from '../logRocket.js';
import * as Sentry from '@sentry/browser';
import useOrgId from 'hooks/useOrgId.js';
import { useLoadingContext } from 'components/ContextLoader/index.jsx';
import Loader from 'components-v3/Loader/index.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';

const SignInPage = lazy(() => import('pages/SignIn/index.jsx'));
const SignOutPage = lazy(() => import('pages/SignOut/index.jsx'));
const PasswordForgetPage = lazy(() => import('pages/PasswordForget/index.jsx'));
const GlobalPage = lazy(() => import('pages-superadmin/Global/index.jsx'));
const ClientUsage = lazy(() => import('pages-clients/ClientUsage/index.jsx'));
const ClientBilling = lazy(() => import('pages-clients/ClientBilling/index.jsx'));
const ClientUsers = lazy(() => import('pages-clients/ClientUsers/index.jsx'));
const ClientUser = lazy(() => import('pages-clients/ClientUser/index.jsx'));
const SAMLProviders = lazy(() => import('pages-superadmin/SAMLProviders/index.jsx'));
const Clients = lazy(() => import('pages-superadmin/Clients/index.jsx'));
const AiTaggingRunsPage = lazy(() => import('pages-superadmin/AiTaggingRuns/index.jsx'));
const AiTaggingRunPage = lazy(() => import('pages-superadmin/AiTaggingRuns/RunPage.jsx'));
const DashboardTranslationsPage = lazy(() => import('pages-superadmin/DashboardDefaultTranslations/index.jsx'));
const Landing = lazy(() => import('pages/Landing/index.js'));
const PasswordResetPage = lazy(() => import('pages/PasswordReset/index.jsx'));
const AuthActionHandlerPage = lazy(() => import('pages/AuthActionHandler/index.jsx'));
const ProductScraperTestPage = lazy(() => import('pages/ProductScraperTest/index.jsx'));
const McpConnectPage = lazy(() => import('pages/McpConnect/index.jsx'));
const ProductKnowledgePage = lazy(() => import('pages/ProductKnowledge/index.jsx'));
const RedirectCallbackPage = lazy(() => import('pages/RedirectCallbackPage/index.jsx'));
const EmbedPlaygroundOneToMany = lazy(() => import('pages/EmbedPlaygroundOneToMany/index.jsx'));
const UGCRightsManagementPage = lazy(() => import('pages/UGCRightsManagementPage/index.jsx'));
const AppsUserAgentSelectorPage = lazy(() => import('pages/AppsUserAgentSelector/index.jsx'));
const StartTrialPage = lazy(() => import('pages/StartTrial/index.jsx'));
const NoTeam = lazy(() => import('pages/UnauthorizedPage/NoTeam.jsx'));
const UnauthorizedPage = lazy(() => import('pages/UnauthorizedPage/401.jsx'));
const LiveRedirect = lazy(() => import('./LiveRedirect.js'));
const CallsRedirect = lazy(() => import('./CallsRedirect.js'));
const EmailNotificationsUnsubscribed = lazy(() => import('pages/EmailNotificationsUnsubscribed/index.jsx'));

const isInternalEmail = (email) => {
  if (email?.endsWith('fb-app-review@bambuser.com')) return false;
  if (email?.endsWith('@bambuser.com')) return true;
  if (email?.endsWith('@one-to-one.test')) return true;
  if (email?.endsWith('@qa-ses.bambuser.com')) return true;
  if (email?.endsWith('@mailslurp.com')) return true;
  return false;
};

const Router = () => {
  const orgId = useOrgId();
  const userId = useSelector(state => state.sessionState.authUser?.uid);
  const email = useSelector(state => state.sessionState.authUser?.email);
  const org = useSelector(state => state.sessionState.authUser?.membership(orgId)?.org);
  const { isLoading, setDelayedLoading } = useLoadingContext();
  const { features: { logRocketEnabled } } = useFeatureContext();

  // Initialize LogRocket and dentify the user
  useEffect(() => {
    if (!userId || !orgId || !logRocketEnabled) return;
    if (isInternalEmail(email) && !window.location.search?.includes('logrocket=1')) {
      return; // Skip Bambuser staff unless logrocket=1 is in the URL (for debugging purposes)
    }
    initLogRocket();
    LogRocket.identify(userId, {
      org_id: orgId,
      org_type: org?.isTrial ? 'trial' : (org?.type || 'default'),
    });
  }, [userId, orgId, email, org, logRocketEnabled]);

  // Attach user info to Sentry scope (only reason we have this here is
  // becuase of the similarity to the LogRocket code above)
  useEffect(() => {
    Sentry.setUser(userId ? { id: userId, email } : null);
    Sentry.setTag('org_id', orgId || null);
  }, [userId, orgId, email]);

  const handleComplete = useCallback(() => {
    setDelayedLoading(false);
  }, [setDelayedLoading]);

  if (isLoading) return <Loader onComplete={handleComplete} completeAfterTime={1200} />;

  return (
    <Suspense fallback={null}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Switch>
          <Route exact path={ROUTES.NO_TEAM}>
            <NoTeam />
          </Route>
          <Route exact path={ROUTES.LANDING}>
            <Landing />
          </Route>
          <Route exact path={ROUTES.SIGN_IN}>
            <SignInPage />
          </Route>
          <Route exact path={ROUTES.SIGN_OUT}>
            <SignOutPage />
          </Route>
          <Route exact path={ROUTES.PASSWORD_RESET}>
            <PasswordResetPage />
          </Route>
          <Route exact path={ROUTES.PASSWORD_FORGET}>
            <PasswordForgetPage />
          </Route>
          <Route exact path={ROUTES.EMAIL_NOTIFICATIONS_UNSUBSCRIBED}>
            <EmailNotificationsUnsubscribed />
          </Route>
          <Route exact path={ROUTES.AUTH_ACTION_HANDLER}>
            <AuthActionHandlerPage />
          </Route>
          <Route exact path={ROUTES.REDIRECT_CALLBACK}>
            <RedirectCallbackPage />
          </Route>
          <Route exact path={ROUTES.PRODUCT_SCRAPER_TEST_PAGE}>
            <ProductScraperTestPage />
          </Route>
          <Route exact path={ROUTES.MCP_CONNECT}>
            <McpConnectPage />
          </Route>
          <Route path={ROUTES.PRODUCT_KNOWLEDGE}>
            <ProductKnowledgePage />
          </Route>
          <Route exact path={ROUTES.EMBED_PLAYGROUND_ONE_TO_MANY}>
            <EmbedPlaygroundOneToMany />
          </Route>
          <Route exact path={ROUTES.DOWNLOAD_APP_WITH_PRODUCT}>
            <AppsUserAgentSelectorPage />
          </Route>
          <Route exact path={ROUTES.START_TRIAL}>
            <StartTrialPage />
          </Route>
          <Route exact path={ROUTES.UGC_RIGHTS_MGMT}>
            <UGCRightsManagementPage />
          </Route>
          {/*SUPERADMIN*/}
          <Route exact path={ROUTES.GLOBAL}>
            <GlobalPage />
          </Route>
          <Route exact path={ROUTES.DASHBOARD_TRANSLATIONS}>
            <DashboardTranslationsPage />
          </Route>
          <Route exact path={ROUTES.SAML_PROVIDERS}>
            <SAMLProviders />
          </Route>
          <Route exact path={ROUTES.CLIENTS}>
            <Clients />
          </Route>
          <Route exact path={ROUTES.AI_TAGGING_RUNS}>
            <AiTaggingRunsPage />
          </Route>
          <Route exact path={ROUTES.AI_TAGGING_RUN}>
            <AiTaggingRunPage />
          </Route>
          {/*Client routes*/}
          <Route exact path={ROUTES.CLIENT_401}>
            <UnauthorizedPage />
          </Route>
          <Route exact path={ROUTES.CLIENT_USAGE}>
            <ClientUsage />
          </Route>
          <Route exact path={ROUTES.CLIENT_BILLING_UPGRADE}>
            <ClientBilling />
          </Route>
          <Route exact path={ROUTES.CLIENT_BILLING}>
            <ClientBilling />
          </Route>
          <Route exact path={ROUTES.CLIENT_USERS}>
            <ClientUsers />
          </Route>
          <Route exact path={ROUTES.CLIENT_USERS_INVITE}>
            <ClientUser />
          </Route>
          <Route exact path={ROUTES.CLIENT_USERS_EDIT}>
            <ClientUser />
          </Route>
          {/*SHORTCUTS*/}
          <Route exact path={ROUTES.SHOWS}>
            <OrgShortcut />
          </Route>
          <Route exact path={ROUTES.SHOW}>
            <OrgShortcut />
          </Route>
          <Route exact path={ROUTES.ON_DEMAND_SHORTCUT}>
            <OrgShortcut />
          </Route>
          <Route exact path={ROUTES.LIVE_SHORTCUT}>
            <OrgShortcut />
          </Route>
          <Route exact path={ROUTES.CALLS_SHORTCUT}>
            <OrgShortcut />
          </Route>
          <Route exact path={ROUTES.USERS}>
            <OrgShortcut />
          </Route>
          <Route exact path={ROUTES.PROFILE}>
            <ProfileShortcut />
          </Route>
          <Route exact path={ROUTES.BILLING_UPGRADE}>
            <ClientShortcut />
          </Route>
          <Route exact path={ROUTES.BILLING}>
            <ClientShortcut />
          </Route>
          <Route exact path={ROUTES.PAGE_404}>
            <NotFoundPage />
          </Route>
          {/*Org routes*/}
          <Route path={ROUTES.REDIRECT_ORG_BASE_ROUTE}>
            <OrgRedirect />
          </Route>
          <Route path={ROUTES.ONE_TO_MANY}>
            <LiveRedirect />
          </Route>
          <Route path={ROUTES.ONE_TO_ONE}>
            <CallsRedirect />
          </Route>
          <Route path={ROUTES.ORG_BASE_ROUTE}>
            <OrgRouter />
          </Route>
          {/*404*/}
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
      </ErrorBoundary>
    </Suspense>
  );
};

export default Router;
